<template>
  <div class="space-y-2" data-test-id="form-sign-in">
    <h2 v-if="$t.contactUsForm.title && showTitle" id="contactUsFormTitle" :class="classTitle">
      {{ $t.contactUsForm.title }}
    </h2>
    <p v-if="$t.contactUsForm.subheading">
      {{ replaceAll($t.contactUsForm.subheading, { brandName: capitalize(brand) }) }}
    </p>
    <p class="text-xs c-grey-20">
      {{ $t.requiredField }}
    </p>
    <base-form
      ref="contactUsFormRef"
      :aria-labelledby="$t.contactUsForm.title && showTitle ? 'contactUsFormTitle' : null"
      class="grid"
      :form
      @submit="submit"
    >
      <div class="grid mb-4 gap-x-3 gap-y-4 md:cols-2">
        <vf-form-field
          v-slot="{ attrs }"
          class-wrapper="overflow-hidden"
          name="category"
          :rule="validateRequired('', $t.validators.requiredSelect)"
        >
          <vf-select
            v-model="form.category"
            v-bind="attrs"
            :disabled="processing"
            :options="categoryOptions"
            required
            @change="stillNeedHelp = false"
          >
            {{ $t.contactUsForm.categoryLabel }}
          </vf-select>
        </vf-form-field>
        <vf-form-field
          v-slot="{ attrs }"
          class-wrapper="overflow-hidden"
          name="subject"
          :rule="form.category ? validateRequired('', $t.validators.requiredSelect) : []"
        >
          <vf-select
            v-model="form.subject"
            v-bind="attrs"
            :disabled="processing || !form.category"
            :options="subjectOptions"
            required
            @change="stillNeedHelp = false"
          >
            {{ $t.contactUsForm.subjectLabel }}
          </vf-select>
        </vf-form-field>
      </div>
      <div
        v-if="hideFormBySubjectList?.includes(form.subject) && !stillNeedHelp"
        class="ws-pre-wrap"
      >
        <base-interpolator :content="getSubjectMessage(form.subject)">
          <template #warranty="{ args: [message] }">
            <vf-link to="warranty">
              {{ message }}
            </vf-link>
          </template>
        </base-interpolator>
        <vf-button class="mx-a mt-6 block" @click="stillNeedHelp = true">
          {{ $t.contactUsForm.stillNeedHelpCta }}
        </vf-button>
      </div>
      <template v-else>
        <div class="grid mb-4 gap-x-3 gap-y-4 md:cols-2">
          <vf-form-field
            v-slot="{ attrs }"
            name="firstName"
            :rule="[
              ...getValidator('firstName', $t.firstName),
              validateRequired($t.firstName),
              validateName($t.validation.firstName),
            ]"
          >
            <vf-input v-model="form.firstName" v-bind="attrs" :disabled="processing" :label="$t.firstName" required />
          </vf-form-field>
          <vf-form-field
            v-slot="{ attrs }"
            name="lastName"
            :rule="[
              ...getValidator('lastName', $t.lastName),
              validateRequired($t.lastName),
              validateName($t.validation.lastName),
            ]"
          >
            <vf-input v-model="form.lastName" v-bind="attrs" :disabled="processing" :label="$t.lastName" required />
          </vf-form-field>
        </div>
        <div class="grid mb-4 gap-x-3 gap-y-4 md:cols-2">
          <vf-form-field
            v-slot="{ attrs }"
            :hint="$t.emailFormatHint"
            name="emailAddress"
            :rule="[...getValidator('email', $t.email), validateEmail()]"
          >
            <input-email v-model="form.emailAddress" v-bind="attrs" :disabled="processing" />
          </vf-form-field>
          <vf-form-field
            v-slot="{ attrs }"
            name="phone"
            :rule="form.phone !== '' ? [validateRequired($t.phone), validatePhone(form.phoneCode)] : []"
          >
            <input-phone
              v-bind="attrs"
              v-model="form.phone"
              v-model:code="form.phoneCode"
              :disabled="processing"
              :label="$t.phone"
            />
          </vf-form-field>
        </div>
        <div class="grid mb-4 gap-x-3 gap-y-4 md:cols-2">
          <vf-form-field v-slot="{ attrs }" name="orderNumber" :rule="[...getValidator('orderNumber', $t.orderNumber)]">
            <vf-input v-model="form.orderNumber" v-bind="attrs" :disabled="processing" :label="$t.orderNumber" />
          </vf-form-field>
        </div>

        <div class="mb-4">
          <vf-form-field
            v-slot="{ attrs }"
            name="message"
            :rule="[validateRequired($t.message), validateRegex(/^[^<>]*$/, $t.noSpecialCharacters)]"
          >
            <vf-textarea
              v-model="form.message"
              v-bind="attrs"
              data-test-id="form-contact-us-message"
              :disabled="processing"
              :label="$t.message"
              required
              resizable
            />
          </vf-form-field>
        </div>
        <!-- reCaptcha -->
        <div class="mb-4">
          <template v-for="{ type, required } in uniqueAgreementCheckboxes" :key="type">
            <vf-form-field
              v-slot="{ attrs }"
              :name="type"
              :rule="required ? [validateRequired('', $t.validators.requiredCheckbox)] : []"
            >
              <vf-checkbox v-model="form[type]" v-bind="attrs" required>
                <form-legal-disclaimer
                  :content="$t.brandAgreement[type]"
                  :data-test-ids="{ privacy: 'form-sign-up-link-loyalty' }"
                />
              </vf-checkbox>
            </vf-form-field>
          </template>
        </div>
        <vf-button class="mb-4 w-full md:w-1/3" :disabled="processing" :loading="processing" type="submit">
          {{ messageSuccessfullySent ? $t.messageSent : $t.sendMessage }}
        </vf-button>
        <span
          v-if="messageSuccessfullySent"
          v-text="$t.messageSent"
          aria-atomic="true"
          aria-live="assertive"
          class="sr-only"
          role="alert"
        />
      </template>
    </base-form>
  </div>
</template>

<script lang="ts" setup>
import type { BaseForm as BaseFormType } from '#components'
import type { GetContactUsCategoriesData } from '#root/api/clients/contact-us/data-contracts'
import type { SelectOption } from '#types/components/base/select'

const { showTitle = true } = defineProps<{ showTitle?: boolean, classTitle?: string }>()

const emit = defineEmits<{
  'display-error': [err: any]
}>()

const { brand } = useRuntimeConfig().public
const { contactUs } = useApi()
const countryCode = useCountryCode()

const initFormObject = () => ({
  category: '',
  emailAddress: '',
  firstName: '',
  lastName: '',
  message: '',
  orderNumber: '',
  phone: '',
  phoneCode: getPhoneCodeByCountryCode(countryCode),
  subject: ''
})

const categoryOptions = ref<SelectOption[]>([])
const subjectOptions = ref<SelectOption[]>([])
const messageSuccessfullySent = ref(false)
const processing = ref(false)
const stillNeedHelp = ref(false)

const contactUsCategoriesResponse = ref<Awaited<GetContactUsCategoriesData>>({} as GetContactUsCategoriesData)
const contactUsFormRef = ref<InstanceType<typeof BaseFormType>>()

const form = reactive(initFormObject())

const { load: loadRules, getValidator } = useAddressApiRules()

const { agreementCheckboxes, hideFormBySubjectList } = useAppConfig().components.form.contactUs

const uniqueAgreementCheckboxes = Array.from(
  new Map(agreementCheckboxes.map((item) => [item.type, item])).values()
)

const { $t, $feature } = useNuxtApp()

const recaptcha = useRecaptcha()
const { validateEmail, validateRequired, validatePhone } = useLocalizedValidators()

const resetForm = () => Object.assign(form, initFormObject())
const parseCategoryOptions = () => {
  categoryOptions.value = (contactUsCategoriesResponse.value?.categories || [])
    .map(({ category: value, translation: label }) => ({ value, label }))
}

const parseSubjectOptions = () => {
  form.subject = ''
  subjectOptions.value = contactUsCategoriesResponse.value.categories
    ?.find((cat) => cat.category === form.category)
    ?.subjects
    .map(({ subject: value, translation: label }) => ({ value, label })) || []
}

onMounted(async () => {
  loadRules(countryCode.toLocaleLowerCase(), useLocale())
  contactUsCategoriesResponse.value = await contactUs.$getContactUsCategories()
  parseCategoryOptions()
})

watch(form, () => messageSuccessfullySent.value = false)

watch(() => form.category, () => {
  parseSubjectOptions()
})

const getCountryLanguage = () => {
  const [language, country] = useLocale().split('-')
  return `${country}-${language}`
}
const preparePayload = () => ({
  category: form.category,
  consumerEmail: form.emailAddress,
  firstName: form.firstName,
  lastName: form.lastName,
  message: form.message,
  orderNumber: form.orderNumber,
  origin: getCountryLanguage(),
  personTitle: '',
  consumerPhone: form.phone ? formatE164phone(form.phone, form.phoneCode) : '',
  subject: form.subject
})

const submit = async () => {
  try {
    emit('display-error', { message: '' })
    processing.value = true

    const recaptcha_response = await recaptcha.execute('sendCustomerCareCase')
    const postPayload = preparePayload()
    await contactUs.$sendCase({ ...postPayload, recaptcha_response })
    resetForm()
    contactUsFormRef.value?.reset()
    await nextTick()
    messageSuccessfullySent.value = true
  }
  catch (err) {
    assertApiError(err)
    emit('display-error', err)
    scrollTo({ top: 0, behavior: 'smooth' })
  }
  finally {
    processing.value = false
  }
}

const camelCase = (value) =>
  value.toLowerCase()
    .replace(/(?:^|\s)\w/g, (match, i) =>
      i === 0 ? match.toLowerCase() : match.trim().toUpperCase())
    .replace(/\s+/g, '')

const getSubjectMessage = (subject: string): string => {
  const key = camelCase(subject)
  if (key === 'exchangeRequest' && $feature.allowExchangeItem)
    return $t.contactUsForm.subjectMessages.exchangeRequestAllowed

  return $t.contactUsForm.subjectMessages[key]
}

defineExpose({
  contactUsFormRef
})
</script>
